import { Button, ButtonStyles, FloatingSnackbar, List, ListItem, ThemesNames } from '@silverfort/sf-components';
import { useServiceAccountContext } from '../contexts/ServiceAccountContext';
import { httpPut } from '../../../../components/utils';
import { useCallback, useMemo, useState } from 'react';
import { ChangeAccountCategoryPopup } from './drawer/Popups';
import { BulkPolicyDialog } from '../components/BulkPolicyDialog';
import { AD_ENTITY_TYPES, SERVICE_ACCOUNT_ACTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { PopupType } from '../components/ActionPopups';
import { useSmartPolicyContext } from '../screens/SmartPolicies/SmartPolicyContext';
import { SmartPolicyCreationFlow, SmartPolicyEntityFullData } from '../screens/SmartPolicies/SmartPoliciesTypes';

const MoreActions = ({
    options
}: {
    options: {
        label: string;
        action: () => void;
    }[];
}) => {
    return (
        <List id="dropdown" theme={ThemesNames.Dark} className="!rounded-lg !bg-sf-gray-900 min-w-[216px]">
            {options.map((option) => (
                <ListItem
                    className="!hover:bg-sf-gray-600 bg-sf-gray-900"
                    onClick={option.action}
                    theme={ThemesNames.Dark}
                    title={option.label}
                />
            ))}
        </List>
    );
};

const OptionButtons = ({
    options
}: {
    options: {
        label: string;
        action: () => void;
    }[];
}) => {
    return (
        <div className="flex flex-row space-x-2">
            {options.map((option, index) => {
                return (
                    <Button key={index} buttonStyle={ButtonStyles.Ghost} text={option.label} onClick={option.action} />
                );
            })}
        </div>
    );
};

export const ServiceAccountActions = ({
    openSmartPolicies,
    setOpenPopup
}: {
    openSmartPolicies: () => void;
    setOpenPopup: (popup: PopupType) => void;
}) => {
    const {
        onServiceAccountUpdate,
        onServiceAccountUpdatePolicy,
        clearSelectedIds,
        selectedServiceAccounts,
        onPremNHILicenseProtectionEnabled,
        setRulesSyncStatus
    } = useServiceAccountContext();
    const { createNewSmartPolicy, smartPolicies, smartPoliciesConfig } = useSmartPolicyContext();
    const [showClassificationModal, setShowClassificationModal] = useState(false);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [showDenyModal, setShowDenyModal] = useState(false);

    // const selectedServiceAccounts = useMemo(() => {
    //     return Object.values(tabs).flatMap((tab) => tab.selectedServiceAccounts);
    // }, [tabs]);
    const changeClassification = useCallback(
        async (selectedType?: string) => {
            await httpPut('/ServiceAccountsV2/updateClassification', {
                serviceAccountsToUpdate: selectedServiceAccounts,
                updatedData: { type_resolved: selectedType }
            });
            clearSelectedIds();
            onServiceAccountUpdate();
        },
        [clearSelectedIds, onServiceAccountUpdate, selectedServiceAccounts]
    );

    const getSmartPolicyEntitiesFromSelectedSas = useCallback((): SmartPolicyEntityFullData[] => {
        return Object.values(selectedServiceAccounts).map((sa) => {
            return {
                guid: sa.guid,
                display_name: sa.display_name,
                type: AD_ENTITY_TYPES.USER,
                domain_name: sa.domain_name,
                excluded: false
            };
        });
    }, [selectedServiceAccounts]);

    const changeClassificationOption = useMemo(() => {
        return [
            {
                label: 'Misclassification',
                action: () => {
                    setOpenPopup('discard');
                }
            },
            {
                label: 'Hide',
                action: () => {
                    setOpenPopup('hide');
                }
            },
            {
                label: 'Change Category',
                action: () => {
                    setShowClassificationModal(true);
                }
            }
        ];
    }, [setOpenPopup, setShowClassificationModal]);

    const nhiProtectionOptions = useMemo(() => {
        const options = [
            {
                label: 'Notify Policy',
                action: () => {
                    setShowNotifyModal(true);
                }
            },
            {
                label: 'Deny Policy',
                action: () => {
                    setShowDenyModal(true);
                }
            }
        ];

        if (smartPoliciesConfig && smartPolicies.length < smartPoliciesConfig?.maxSmartPolicies) {
            options.push({
                label: 'Create Smart Policy',
                action: () => {
                    openSmartPolicies();
                    createNewSmartPolicy({
                        entities: getSmartPolicyEntitiesFromSelectedSas(),
                        creationFlow: SmartPolicyCreationFlow.BULK
                    });
                }
            });
        }

        return options;
    }, [
        setShowNotifyModal,
        setShowDenyModal,
        getSmartPolicyEntitiesFromSelectedSas,
        openSmartPolicies,
        createNewSmartPolicy,
        smartPoliciesConfig,
        smartPolicies.length
    ]);

    return (
        <div>
            <FloatingSnackbar
                menuContent={
                    onPremNHILicenseProtectionEnabled ? <MoreActions options={changeClassificationOption} /> : undefined
                }
                open={!!selectedServiceAccounts.length}
                onClose={() => clearSelectedIds()}
                selections={selectedServiceAccounts.length}>
                <div>
                    {onPremNHILicenseProtectionEnabled ? (
                        <OptionButtons options={nhiProtectionOptions} />
                    ) : (
                        <OptionButtons options={changeClassificationOption} />
                    )}
                </div>
            </FloatingSnackbar>

            <ChangeAccountCategoryPopup
                open={showClassificationModal}
                onClose={() => setShowClassificationModal(false)}
                onApprove={changeClassification}
            />

            <BulkPolicyDialog
                onClose={() => {
                    setRulesSyncStatus('loading')
                    setShowNotifyModal(false);
                    clearSelectedIds();
                }}
                open={showNotifyModal}
                action={SERVICE_ACCOUNT_ACTIONS.LOG.ID}
                title="Activate Notify Policies"
                content="Select Yes to activate a Notify policy for each of the selected service accounts. Sources, destinations and protocols active in the last 90 days will be allowed."
            />
            <BulkPolicyDialog
                onClose={() => {
                    setShowDenyModal(false);
                    onServiceAccountUpdatePolicy(selectedServiceAccounts.map((selectedSA) => selectedSA.guid));
                    clearSelectedIds();
                }}
                open={showDenyModal}
                action={SERVICE_ACCOUNT_ACTIONS.BLOCK.ID}
                title="Activate Deny Policies"
                content="Select Yes to activate a Deny policy for each of the selected Service Accounts. Sources, destinations and protocols active in the last 90 days will be allowed."
            />
        </div>
    );
};
